import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { dishesService } from "../../../services/dishes-services";
import { dishesActions } from "./dishes.slice";

// Batching exists purely so the progress bar moves — the backend would happily
// take all 2000 in one request. A fixed size meant small files finished in a
// single step and looked frozen, so instead aim for a roughly constant number
// of updates whatever the file size. Capped at 200 to keep payloads sane and
// floored at 3 so a tiny file still animates (a 10-dish file gets 4 steps
// instead of 1; from ~90 dishes up the floor stops mattering).
const TARGET_STEPS = 8;
const batchSizeFor = (total) => Math.min(200, Math.max(3, Math.ceil(total / TARGET_STEPS)));

const initialState = {
    // "idle" | "importing" | "done" — drives the floating chip in MainLayout.
    // This lives in Redux rather than in the modal so that navigating away from
    // /dishes does not unmount the progress UI mid-import.
    phase: "idle",
    current: 0,
    total: 0,
    batch: 0,
    batchCount: 0,
    results: null,
    startedAt: null,
};

/**
 * Runs the whole import. Deliberately a thunk and not component code: the
 * loop must outlive the modal and the /dishes route.
 */
const runDishImport = createAsyncThunk(
    "dishImportSlice/runDishImport",
    async ({ dishes }, { dispatch }) => {
        const total = dishes.length;
        const batchSize = batchSizeFor(total);
        const batchCount = Math.ceil(total / batchSize);
        const startedAt = Date.now();

        dispatch(dishImportActions.started({ total, batchCount, startedAt }));

        const totals = { created: 0, updated: 0, skipped: 0, failed: 0 };
        const errors = [];

        for (let i = 0; i < dishes.length; i += batchSize) {
            const batch = dishes.slice(i, i + batchSize);
            const batchNo = i / batchSize + 1;
            // Announce the batch before awaiting it, so the label reflects what
            // is actually in flight rather than what already finished.
            dispatch(dishImportActions.progress({ current: i, batch: batchNo }));
            try {
                const res = await dishesService.bulkUpsertDishes(batch);
                totals.created += res?.created || 0;
                totals.updated += res?.updated || 0;
                totals.skipped += res?.skipped || 0;
                totals.failed += res?.failed || 0;
                (res?.errors || []).forEach((e) => errors.push(`${e.name}: ${e.message}`));
            } catch (err) {
                // A rejected batch is a whole-batch failure — the server either
                // validated it away (400) or the transaction rolled back.
                const msg = err?.response?.data?.message || err.message;
                totals.failed += batch.length;
                errors.push(`Batch ${batchNo} (${batch.length} dishes): ${msg}`);
            }
            dispatch(
                dishImportActions.progress({
                    current: Math.min(i + batch.length, total),
                    batch: batchNo,
                })
            );
        }

        dispatch(
            dishImportActions.finished({
                successCount: totals.created + totals.updated + totals.skipped,
                createdCount: totals.created,
                updatedCount: totals.updated,
                skippedCount: totals.skipped,
                errors,
                total,
                seconds: ((Date.now() - startedAt) / 1000).toFixed(1),
            })
        );

        if (totals.created + totals.updated > 0) {
            dispatch(dishesActions.changeTrigger());
        }
    }
);

const dishImportSlice = createSlice({
    name: "dishImportSlice",
    initialState,
    reducers: {
        started: (state, action) => {
            state.phase = "importing";
            state.total = action.payload.total;
            state.batchCount = action.payload.batchCount;
            state.startedAt = action.payload.startedAt;
            state.current = 0;
            state.batch = 1;
            state.results = null;
        },
        progress: (state, action) => {
            state.current = action.payload.current;
            state.batch = action.payload.batch;
        },
        finished: (state, action) => {
            state.phase = "done";
            state.current = state.total;
            state.results = action.payload;
        },
        dismiss: () => initialState,
    },
});

const { reducer: dishImportReducer, actions } = dishImportSlice;
const dishImportActions = {
    ...actions,
    runDishImport,
};

export { dishImportReducer, dishImportActions };
